import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const getSuggestionLink = (s) => {
  if (s.type === 'item') return `/item/${s.inventoryNumber || s.id}`;
  if (s.type === 'product') return `/products/${s.productNumber}`;
  if (s.type === 'service') return `/services/${s.serviceId || s.id}`;
  if (s.type === 'tour') return `/tours/${s.tourNumber}`;
  return '/search';
};

const SearchField = ({
  value,
  onChange,
  onSubmit,
  onNavigate,
  branchId = '',
  placeholder = 'Поиск по каталогу…',
  sx = {},
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const isFocusedRef = useRef(false);
  const suppressSuggestRef = useRef(false);
  const dirtyRef = useRef(false);
  const lastCommittedRef = useRef('');
  const skipBlurSubmitRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      lastCommittedRef.current = String(value || '').trim();
    }
  }, [value]);

  const closeSuggestions = useCallback(() => {
    suppressSuggestRef.current = true;
    fetchSeqRef.current += 1;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setOpen(false);
    setLoadingSuggest(false);
  }, []);

  const fetchSuggestions = useCallback(
    async (rawQuery) => {
      const q = String(rawQuery || '').trim();
      if (q.length < 2 || suppressSuggestRef.current || !isFocusedRef.current) {
        setSuggestions([]);
        setOpen(false);
        return;
      }

      const seq = ++fetchSeqRef.current;
      setLoadingSuggest(true);

      try {
        const params = new URLSearchParams({ q, limit: '8' });
        if (branchId) params.set('branchId', branchId);
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/api/search/suggest?${params.toString()}`
        );
        const data = await res.json().catch(() => ({}));

        if (
          seq !== fetchSeqRef.current ||
          suppressSuggestRef.current ||
          !isFocusedRef.current
        ) {
          return;
        }

        const next = data.ok ? data.suggestions || [] : [];
        setSuggestions(next);
        setOpen(next.length > 0);
      } catch {
        if (seq === fetchSeqRef.current) {
          setSuggestions([]);
          setOpen(false);
        }
      } finally {
        if (seq === fetchSeqRef.current) setLoadingSuggest(false);
      }
    },
    [branchId]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const q = String(value || '').trim();
    if (q.length < 2 || suppressSuggestRef.current || !isFocusedRef.current) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(q);
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, branchId, fetchSuggestions]);

  useEffect(() => {
    const handlePointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        closeSuggestions();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [closeSuggestions]);

  const getCurrentQuery = () =>
    String(inputRef.current?.value ?? value ?? '').trim();

  const commitSearch = (rawQuery) => {
    const q = String(rawQuery ?? getCurrentQuery()).trim();
    if (q.length < 1) return;
    if (q === lastCommittedRef.current) {
      closeSuggestions();
      return;
    }
    lastCommittedRef.current = q;
    dirtyRef.current = false;
    suppressSuggestRef.current = true;
    closeSuggestions();
    skipBlurSubmitRef.current = true;
    onSubmit?.(q);
    setTimeout(() => {
      skipBlurSubmitRef.current = false;
    }, 300);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    commitSearch(getCurrentQuery());
    inputRef.current?.blur();
  };

  const handlePick = (s) => {
    skipBlurSubmitRef.current = true;
    dirtyRef.current = false;
    closeSuggestions();
    inputRef.current?.blur();
    onNavigate?.(getSuggestionLink(s));
    setTimeout(() => {
      skipBlurSubmitRef.current = false;
    }, 300);
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
    suppressSuggestRef.current = false;
    const q = String(value || '').trim();
    if (q.length >= 2) {
      fetchSuggestions(q);
    } else if (suggestions.length > 0) {
      setOpen(true);
    }
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    // На мобильных «Готово» часто только снимает фокус, не отправляя form submit
    setTimeout(() => {
      if (!isFocusedRef.current) {
        closeSuggestions();
        if (skipBlurSubmitRef.current) return;
        const q = getCurrentQuery();
        if (dirtyRef.current && q.length >= 1) {
          commitSearch(q);
        }
      }
    }, 180);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitSearch(getCurrentQuery());
      inputRef.current?.blur();
    }
  };

  return (
    <Box ref={wrapRef} component="form" onSubmit={handleSubmit} sx={{ position: 'relative', ...sx }}>
      <TextField
        inputRef={inputRef}
        fullWidth
        size="small"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          suppressSuggestRef.current = false;
          dirtyRef.current = true;
          onChange?.(e.target.value);
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        enterKeyHint="search"
        type="search"
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '4px',
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
            '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.5)' },
            '&.Mui-focused fieldset': { borderColor: 'action.active' },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#000000' }} />
            </InputAdornment>
          ),
        }}
      />
      {open && suggestions.length > 0 && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 1300,
            maxHeight: 320,
            overflow: 'auto',
          }}
        >
          <List dense disablePadding>
            {suggestions.map((s, idx) => (
              <ListItemButton
                key={`${s.type}-${s.id || idx}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handlePick(s)}
              >
                <ListItemText
                  primary={s.label}
                  secondary={s.typeLabel}
                  primaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
          {loadingSuggest && (
            <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
              Загрузка…
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SearchField;
